// components/slides/SlidesCanvas.tsx
'use client';

// Center stage for the focused page (dark Figma-Slides reference): the slide
// as a big centered card (rounded, ring-1 white/10, shadow-xl) dominating the
// black stage, with ←/→ page navigation. The horizontal Studio-style toolbar
// (SlidesToolbar) is rendered by SlidesEditor pinned to the bottom of the
// stage; the fit's margins clear it. The immutable base (PDF snapshot) is scaled by zoom,
// plus the overlay element tree in PAGE-POINT coordinates mapped to CSS px
// (left = x * scale - no CSS transform, so selection chrome and note markers
// keep a constant screen size at any zoom).
//
// Interaction model:
// - select tool: click selects, drag moves (px deltas / scale → point deltas,
//   committed on pointer-up), Escape deselects, Delete removes.
// - note/text/stamp tools: click places at the point + opens an inline editor
//   (empty commit removes the element); arrow/rect: click-drag defines the
//   vector/frame. Placement always
//   drops back to the select tool.
// - ctrl/cmd + wheel zooms (fit-relative); plain scroll pans the container.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { uploadFileToCloud } from '#lib/azure-client';
import { resolveStampFrame } from '#lib/slides/annotationLayout';
import { resizeFrameBy, type ResizeHandle } from '#lib/slides/resizeFrame';
import { measureNoteText, NOTE_FONT_PT, NOTE_LINE_STEP, NOTE_PAD_PT, pageScaleFromA4 } from '#lib/slides/noteMetrics';
import { buildNoteLayout, NOTE_OVERFLOW_MESSAGE } from '#lib/slides/noteLayout';
import { buildStampArtwork } from '#lib/slides/stampArtwork';
import { STAMP_LINE_HEIGHT, STAMP_OVERFLOW_MESSAGE } from '#lib/slides/stampLayout';
import { buildElementLayout, ELEMENT_OVERFLOW_MESSAGE } from '#lib/slides/elementLayout';
import { copiedElementsText, pastedElements } from '#lib/slides/elementClipboard';
import { BLOCK_LIBRARY, buildBlockArtwork } from '#lib/slides/blockArtwork';
import { standardFontStyle } from '#lib/slides/standardText';
import type {
  SlideDeckDTO,
  SlideElement,
  SlideElementFrame,
  SlidePageDoc,
  SlidesTool,
} from '#types/types';
import { slidesGenId, useSlidesStore } from './slidesStore';
import { useSlidesAuthor } from './useSlidesAuthor';
import {
  ArtworkShapes,
  DEFAULT_INK,
  NOTE_SEVERITY_COLORS,
  SlideArrow,
  TEXT_INK,
  ZOOM_MAX,
  ZOOM_MIN,
  normFrame,
} from './slidesUi';
import { SlidesValidationCard, buildStampCardLayout } from './SlidesValidationCard';
import { useKernelStore } from '@/kernel/kernelStores';
// The Analyst's follow-the-agent chrome (aura pulse, gliding Fater cursor,
// vanish animations) - reused verbatim so both tools share one visual system.
import agentFx from '../tables/TableGrid.module.css';
import slidesFx from './SlidesCanvas.module.css';
import { agentIsWorking } from '#lib/chat/agentIsWorking';

/** Live "agent mid-loop" signal - same session-state derivation as the
 *  Analyst's glow (streaming chatState || an executing script). */
const useSlidesSessionBusy = (): boolean =>
  useKernelStore((state) => {
    const sid = state.activeChatSessionId;
    const rt = sid ? state.inventory.ChatSession.registry[sid]?.runtime : null;
    return agentIsWorking(rt?.chatState, rt?.runningScriptId);
  });

/** Writing pace for agent callouts: ~18 characters a second, plus jitter and a
 *  beat after punctuation. Fast enough to sit through, slow enough to watch. */
const MS_PER_CHAR = 55;
/** How far in to go while a callout is being written. A floor, not a fit: see
 *  the note at the zoom effect for why fitting the pair is not enough. */
/** Matches WHEEL_ZOOM_FACTOR in kernel/useViewports: the zoom a FULL mouse notch
 *  (~100px of wheel delta) produces, and the per-event ceiling. */
const WHEEL_ZOOM_FACTOR = 1.4;
/** ln(1.4)/100 - so 100px of delta is exactly one Studio notch. */
const ZOOM_PER_WHEEL_PX = Math.log(WHEEL_ZOOM_FACTOR) / 100;
const MIN_WRITE_ZOOM = 2.6;
const MAX_WRITE_ZOOM = 4;
const SENTENCE_PAUSE_MS = 320;
const CLAUSE_PAUSE_MS = 130;

const edited = (value: string, initial: string) => value.replace(/\r\n?/g, '\n') === initial.replace(/\r\n?/g, '\n') ? initial : value;

/** Inline editor for note/text/stamp content. Commits on blur; Escape reverts.
 *  Enter commits for annotation chips, but inserts a newline in a slide text
 *  box (where Cmd/Ctrl+Enter is the commit). */

const InlineTextEditor = React.memo(function InlineTextEditor({
  initial,
  placeholder,
  className,
  /** Placeholder tint - annotation chips sit on dark ink, slide text sits on
   *  the white sheet, so a single hard-coded colour is invisible in one of the
   *  two cases. */
  placeholderClassName = 'placeholder:text-white/30',
  style,
  /** Slide text boxes are real prose: Enter must insert a newline. Annotation
   *  chips (note / stamp) stay single-shot, where Enter commits. */
  multiline = false,
  /** True when the element was just placed: its default text ("STAMP") should
   *  be selected so typing REPLACES it. Re-editing an existing element puts
   *  the caret at the end instead, so typing appends. */
  selectOnMount = false,
  /** Go back / Confirm under the editor's positioned container, bottom right. */
  actions = false,
  onCommit,
}: {
  initial: string;
  placeholder?: string;
  className?: string;
  placeholderClassName?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  selectOnMount?: boolean;
  actions?: boolean;
  onCommit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  // Escape must revert, not save - but it blurs, and blur is what commits, so
  // the intent has to survive the trip.
  const cancelledRef = useRef(false);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const mountedAtRef = useRef(0);

  // `autoFocus` alone lost the race with the placing click: the element is
  // created on pointer-down, React mounts this focused textarea, and then the
  // browser's own focus handling for the following mouse-up moved focus to the
  // page surface. That blur committed an empty string, which DELETES a freshly
  // placed note or text box - so those two tools looked completely broken
  // while stamp (which ships with default text) appeared to work.
  useEffect(() => {
    mountedAtRef.current = performance.now();
    const id = requestAnimationFrame(() => {
      const el = areaRef.current;
      if (!el) return;
      el.focus();
      if (selectOnMount) {
        el.select();
      } else {
        // Caret to the end. Selecting everything meant the first keystroke
        // after a double-click erased the existing text.
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [selectOnMount]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value), []);
  const onBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    // Only a new placement losing focus to the page needs protection. An
    // intentional move to another control must close even an unchanged editor.
    if (selectOnMount && !event.relatedTarget && value === initial && performance.now() - mountedAtRef.current < 350) {
      requestAnimationFrame(() => areaRef.current?.focus());
      return;
    }
    onCommit(edited(value, initial));
  }, [initial, onCommit, selectOnMount, value]);
  const finish = useCallback((save: boolean) => {
    cancelledRef.current = true;
    areaRef.current?.blur();
    onCommit(save ? edited(value, initial) : initial);
  }, [initial, onCommit, value]);
  const onPointerDown = useCallback((e: React.PointerEvent) => e.stopPropagation(), []);
  // The buttons must not take focus: the textarea's blur is itself a commit.
  const keepFocus = useCallback((e: React.PointerEvent | React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }, []);
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
        return;
      }
      if (e.key !== 'Enter') return;
      // Multiline: Enter = newline, Cmd/Ctrl+Enter = done.
      if (multiline) {
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        return;
      }
      if (!e.shiftKey) {
        e.preventDefault();
        (e.target as HTMLTextAreaElement).blur();
      }
    },
    [finish, multiline]
  );

  return (
    <>
    <textarea
      ref={areaRef}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      onBlur={onBlur}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={`${slidesFx.inlineEditor} resize-none bg-transparent outline-none ${placeholderClassName} ${className ?? ''}`}
      style={{ ...style, ['--slides-editor-font' as string]: style?.fontFamily ?? 'Helvetica, Arial, sans-serif' }}
    />
    {actions && <div className="absolute flex gap-1.5" style={{ top: 'calc(100% + 6px)', right: 0 }} onPointerDown={keepFocus} onMouseDown={keepFocus}>
      <button type="button" onClick={() => finish(false)} className="h-7 rounded-full bg-[#2A2A2A] px-3 text-[#E8E8E8] transition-colors duration-150 hover:bg-[#3A3A3A]">Go back</button>
      <button type="button" onClick={() => finish(true)} className="h-7 rounded-full bg-[#3B82F6] px-3 text-white transition-colors duration-150 hover:bg-[#2563EB]">Confirm</button>
    </div>}
    </>
  );
});

/** Who wrote a callout by hand, on its bottom-right corner. Screen only: the export draws the doc, not this.
 *  The photo is UserAvatar's own endpoint: importing UserAvatar would pull the kernel into the canvas tests. */
const NoteAuthor = React.memo(function NoteAuthor({ author }: { author: NonNullable<SlideElement['writtenBy']> }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const initials = author.name.split(/\s+/).map(word => word[0] ?? '').join('').slice(0, 2).toUpperCase();
  return (
    <div aria-hidden className="pointer-events-none absolute flex max-w-[160px] items-center gap-1 rounded-full border border-white/10 bg-[#2A2A2A] py-0.5 pl-0.5 pr-2"
      style={{ right: 6, bottom: -11 }}>
      {photoFailed
        ? <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3A3A3A] text-[#E8E8E8]" style={{ fontSize: 8 }}>{initials}</span>
        : <img src={`/api/user/avatar?userId=${encodeURIComponent(author.userId)}`} alt="" onError={() => setPhotoFailed(true)} className="h-4 w-4 shrink-0 rounded-full object-cover" />}
      <span className="truncate text-[#E8E8E8]" style={{ fontSize: 11 }}>{author.name}</span>
    </div>
  );
});

interface DragState {
  id: string;
  startX: number;
  startY: number;
  frame: SlideElementFrame;
  moved: boolean;
}

/** Box handles for framed elements; p0/p1 are an arrow's two endpoints. */

const BOX_HANDLES: { key: ResizeHandle; fx: number; fy: number; cursor: string }[] = [
  { key: 'nw', fx: 0, fy: 0, cursor: 'nwse-resize' },
  { key: 'n', fx: 0.5, fy: 0, cursor: 'ns-resize' },
  { key: 'ne', fx: 1, fy: 0, cursor: 'nesw-resize' },
  { key: 'e', fx: 1, fy: 0.5, cursor: 'ew-resize' },
  { key: 'se', fx: 1, fy: 1, cursor: 'nwse-resize' },
  { key: 's', fx: 0.5, fy: 1, cursor: 'ns-resize' },
  { key: 'sw', fx: 0, fy: 1, cursor: 'nesw-resize' },
  { key: 'w', fx: 0, fy: 0.5, cursor: 'ew-resize' },
];

const HandleDot = React.memo(function HandleDot({
  el,
  handle,
  left,
  top,
  cursor,
  onDown,
  onMove,
  onUp,
  onCancel,
}: {
  el: SlideElement;
  handle: ResizeHandle;
  left: number;
  top: number;
  cursor: string;
  onDown: (e: React.PointerEvent, el: SlideElement, handle: ResizeHandle) => void;
  onMove: (e: React.PointerEvent) => void;
  onUp: (e: React.PointerEvent) => void;
  onCancel?: () => void;
}) {
  const onPointerDown = useCallback((e: React.PointerEvent) => onDown(e, el, handle), [onDown, el, handle]);
  const style = useMemo(
    () => ({ zIndex: 100001, left: left - 4, top: top - 4, width: 8, height: 8, cursor, touchAction: 'none' as const, pointerEvents: 'auto' as const }),
    [left, top, cursor]
  );
  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      className="absolute rounded-[2px] border border-[#0033A0] bg-white"
      style={style}
    />
  );
});

const ResizeHandles = React.memo(function ResizeHandles({
  el,
  b,
  frame,
  scale,
  onDown,
  onMove,
  onUp,
  onCancel,
}: {
  el: SlideElement;
  b: SlideElementFrame;
  frame: SlideElementFrame;
  scale: number;
  onDown: (e: React.PointerEvent, el: SlideElement, handle: ResizeHandle) => void;
  onMove: (e: React.PointerEvent) => void;
  onUp: (e: React.PointerEvent) => void;
  onCancel?: () => void;
}) {
  const dot = (key: string, left: number, top: number, cursor: string, handle: ResizeHandle) => (
    <HandleDot
      key={key}
      el={el}
      handle={handle}
      left={left}
      top={top}
      cursor={cursor}
      onDown={onDown}
      onMove={onMove}
      onUp={onUp}
      onCancel={onCancel}
    />
  );

  // An arrow is a vector: its two endpoints are the only meaningful handles.
  if (el.type === 'arrow') {
    return (
      <>
        {dot('p0', frame.x * scale, frame.y * scale, 'move', 'p0')}
        {dot('p1', (frame.x + frame.w) * scale, (frame.y + frame.h) * scale, 'move', 'p1')}
      </>
    );
  }
  return (
    <>
      {BOX_HANDLES.map((h) =>
        dot(h.key, (b.x + b.w * h.fx) * scale, (b.y + b.h * h.fy) * scale, h.cursor, h.key)
      )}
    </>
  );
});

interface SlidesCanvasElementProps {
  el: SlideElement;
  /** Live frame while THIS element is dragged/resized, `el.frame` by identity
   *  otherwise - which is what keeps this prop stable for every element the
   *  pointer is not touching, and therefore what makes the memo below bite. */
  frame: SlideElementFrame;
  scale: number;
  tool: SlidesTool;
  /** Paint order (see the map site in SlidesCanvas). */
  z: number;
  isSelected: boolean;
  isEditing: boolean;
  selectOnMount: boolean;
  pageWidth: number;
  pageHeight: number;
  /** How much of this callout is written so far; undefined = not being typed. */
  typingChars: number | undefined;
  onPointerDownEl: (e: React.PointerEvent, el: SlideElement, isEditing: boolean) => void;
  onPointerMoveEl: (e: React.PointerEvent, id: string) => void;
  onPointerUpEl: (e: React.PointerEvent, id: string) => void;
  onStartEdit: (id: string) => void;
  onCommitText: (id: string, value: string, field?: string) => void;
  onAnchorDown: (e: React.PointerEvent, el: SlideElement) => void;
  onHandleDown: (e: React.PointerEvent, el: SlideElement, handle: ResizeHandle) => void;
  onHandleMove: (e: React.PointerEvent) => void;
  onHandleUp: (e: React.PointerEvent) => void;
  onCancelGesture: () => void;
}

/** One overlay element. Memoized because the canvas re-renders on every
 *  pointer-move of a drag, every frame of the write-zoom animation and every
 *  character of the agent typing loop: without this, all of those rebuilt the
 *  whole element tree. Every prop above is a primitive or an identity-stable
 *  value, and every handler is a parent `useCallback`, so an element only
 *  re-renders when something about IT changed. */
const SlidesCanvasElement = React.memo(function SlidesCanvasElement({
  el,
  frame,
  scale,
  tool,
  z,
  isSelected,
  isEditing,
  selectOnMount,
  pageWidth,
  pageHeight,
  typingChars,
  onPointerDownEl,
  onPointerMoveEl,
  onPointerUpEl,
  onStartEdit,
  onCommitText,
  onAnchorDown,
  onHandleDown,
  onHandleMove,
  onHandleUp,
  onCancelGesture,
}: SlidesCanvasElementProps) {
  const id = el.id;
  const imageUrl = el.type === 'image' ? `/api/slides/image-preview?url=${encodeURIComponent(el.url)}` : null;
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const imageFailed = Boolean(imageUrl && failedImageUrl === imageUrl);
  const noteLayout = useMemo(() => el.type === 'note' ? buildNoteLayout({ ...el, frame }, pageWidth, pageHeight) : null, [el, frame, pageWidth, pageHeight]);
  const elementLayout = useMemo(() => el.type !== 'note' && el.type !== 'block' ? buildElementLayout({ ...el, frame }) : null, [el, frame]);
  const blockArtwork = useMemo(() => el.type === 'block' ? buildBlockArtwork(el.block, el.fields, Math.abs(frame.w), Math.abs(frame.h)) : null, [el, frame]);
  // Which cell of a library stamp is open for typing; the rest of the stamp stays drawn.
  const [blockField, setBlockField] = useState<string | null>(null);
  const displayedElement = useMemo(() => noteLayout ? { ...el, frame: noteLayout.frame } : el, [el, noteLayout]);
  const b = useMemo(() => normFrame(noteLayout?.frame ?? frame), [frame, noteLayout]);
  const handlers = useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => onPointerDownEl(e, displayedElement, isEditing),
      onPointerMove: (e: React.PointerEvent) => onPointerMoveEl(e, id),
      onPointerUp: (e: React.PointerEvent) => onPointerUpEl(e, id),
      onPointerCancel: onCancelGesture,
    }),
    [displayedElement, id, isEditing, onPointerDownEl, onPointerMoveEl, onPointerUpEl, onCancelGesture]
  );
  const startEdit = useCallback(() => onStartEdit(id), [onStartEdit, id]);
  const commit = useCallback((value: string) => onCommitText(id, value), [onCommitText, id]);
  const anchorDown = useCallback((e: React.PointerEvent) => onAnchorDown(e, el), [onAnchorDown, el]);

  const ink = el.color ?? DEFAULT_INK;
  // Explicit colour wins, severity is the default - same rule as the exporter.
  const noteColor = el.type === 'note' ? el.color ?? NOTE_SEVERITY_COLORS[el.severity ?? 'info'] : ink;
  // WYSIWYG, and it has to be EXACT. The frame height was measured for
  // NOTE_FONT_PT text at NOTE_LINE_STEP with NOTE_PAD_PT padding
  // (lib/slides/noteMetrics, the same numbers the PDF draws with), so the
  // canvas has to render those same numbers scaled.
  //
  // It used to render `max(9, min(15, 8.5 * scale))` with a hard-coded 4px
  // pad and 1.25 line height. At a normal fit zoom for an A3 sheet the
  // scale is ~0.6, so 8.5*0.6 = 5.1px was floored to 9 - text 1.8x too
  // large for its box. The box has minHeight, so it grew, and bubbles the
  // layout had placed with clean gaps collided ON SCREEN while their
  // stored frames never overlapped at all. That is the "layout completely
  // off" the reviewer saw: a rendering bug wearing a layout bug's clothes.
  // ...and those numbers are themselves page-relative (pageScaleFromA4):
  // a callout on an A0 sheet is drawn 4x the A4 size in page points, so
  // the canvas must apply that factor before the viewport zoom or the
  // on-screen bubble no longer matches the PDF it exports to.
  const noteScale = pageScaleFromA4(pageWidth, pageHeight);
  const noteFontPx = NOTE_FONT_PT * noteScale * scale;
  const notePadPx = NOTE_PAD_PT * noteScale * scale;
  const editorStyle = useMemo<React.CSSProperties>(() => ({ fontSize: noteFontPx, lineHeight: NOTE_LINE_STEP, color: noteColor }), [noteFontPx, noteColor]);

  const selectionOutline = isSelected ? (
    <div key={`sel-${el.id}`} className="pointer-events-none absolute inset-0" style={{ zIndex: 100000,
      transform: `rotate(${el.rotation ?? 0}deg)`, transformOrigin: `${(b.x + b.w / 2) * scale}px ${(b.y + b.h / 2) * scale}px` }}>
      <div
        className="pointer-events-none absolute rounded-[2px]"
        style={{
          zIndex: 100000,
          left: b.x * scale - 3,
          top: b.y * scale - 3,
          width: b.w * scale + 6,
          height: b.h * scale + 6,
          boxShadow: '0 0 0 1.5px #B3D9FF',
        }}
      />
      {tool === 'select' && !isEditing && (
        <ResizeHandles
          el={el}
          b={b}
          frame={frame}
          scale={scale}
          onDown={onHandleDown}
          onMove={onHandleMove}
          onUp={onHandleUp}
          onCancel={onCancelGesture}
        />
      )}
    </div>
  ) : null;

  switch (el.type) {
    case 'note': {
      // Queued but not yet its turn: the bubble has not been "placed" yet, so
      // nothing is drawn. This is what makes callouts arrive one at a time
      // instead of the whole review appearing at once.
      if (typingChars === 0) return null;
      const c = noteColor;
      const layout = noteLayout!;
      const { anchor, leader } = layout;
      let shown = 0;
      return (
        <>
          <svg aria-hidden className="pointer-events-none absolute" viewBox={`0 0 ${pageWidth} ${pageHeight}`}
            style={{ left: 0, top: 0, width: pageWidth * scale, height: pageHeight * scale, overflow: 'hidden', zIndex: z }}>
            {leader && <line x1={leader.start.x} y1={leader.start.y} x2={leader.end.x} y2={leader.end.y} stroke={c} strokeWidth={layout.strokeWidth} strokeOpacity={layout.opacity} />}
            {anchor && <circle cx={anchor.x} cy={anchor.y} r={layout.anchorRadius} fill={c} fillOpacity={layout.opacity} />}
            <g transform={`translate(${b.x} ${b.y}) rotate(${layout.rotation} ${b.w / 2} ${b.h / 2})`}>
              <path d={layout.path} fill="#fff" fillOpacity={layout.fillOpacity} stroke={c} strokeWidth={layout.strokeWidth} strokeOpacity={layout.opacity} />
              {!isEditing && layout.lines.map((line, index) => {
                const visible = typingChars === undefined ? line : line.slice(0, Math.max(0, typingChars - shown));
                shown += line.length + 1;
                return <text key={index} data-note-line={index} x={layout.padding} y={layout.padding + layout.fontSize + index * layout.lineHeight}
                  fill={c} fillOpacity={layout.opacity} fontFamily="Helvetica, Arial, sans-serif" fontSize={layout.fontSize}
                  textLength={visible ? (typingChars === undefined ? layout.lineWidths[index] : measureNoteText(visible, layout.fontSize)) : undefined} lengthAdjust="spacingAndGlyphs"
                  style={{ fontKerning: 'none', fontVariantLigatures: 'none', whiteSpace: 'pre' }}>{visible}</text>;
              })}
            </g>
          </svg>
          {anchor && <div onPointerDown={anchorDown} title="Drag to move where this comment points"
            className="absolute rounded-full" style={{ left: anchor.x * scale - 6, top: anchor.y * scale - 6, width: 12, height: 12,
              background: isSelected ? c : 'transparent', border: isSelected ? '2px solid #fff' : undefined,
              cursor: 'grab', touchAction: 'none', zIndex: z + 2 }} />}
          <div
            {...handlers}
            data-note-bubble={el.id}
            role="group"
            aria-label={isEditing ? undefined : `Comment${el.writtenBy ? ` by ${el.writtenBy.name}` : ''}: ${el.text}`}
            data-note-overflow={layout.overflow || undefined}
            onDoubleClick={startEdit}
            className="absolute"
            style={{
              transform: `rotate(${layout.rotation}deg)`, transformOrigin: 'center',
              zIndex: z + 1,
              left: b.x * scale, top: b.y * scale, width: b.w * scale, height: b.h * scale,
              cursor: tool === 'select' ? 'move' : undefined,
              touchAction: 'none',
            }}
          >
            {isEditing && !layout.overflow && (
              <InlineTextEditor
                initial={el.text}
                selectOnMount={selectOnMount}
                placeholder="Comment…"
                placeholderClassName="placeholder:text-[#64748B]"
                multiline
                actions
                className="absolute"
                style={{ ...editorStyle, left: notePadPx, top: notePadPx, width: Math.max(40, (b.w - 2 * layout.padding) * scale), height: Math.max(48, Math.min(b.h - 2 * layout.padding, pageHeight * 0.6) * scale), fontFamily: 'Helvetica, Arial, sans-serif', background: '#fff', overflow: 'auto' }}
                onCommit={commit}
              />
            )}
            {!isEditing && el.writtenBy && <NoteAuthor author={el.writtenBy} />}
          </div>
          {isSelected && <div className="pointer-events-none absolute" style={{ zIndex: 100000, left: b.x * scale, top: b.y * scale, width: b.w * scale, height: b.h * scale,
            transform: `rotate(${layout.rotation}deg)`, transformOrigin: 'center', outline: '1.5px solid #B3D9FF', outlineOffset: 3 }}>
            {tool === 'select' && !isEditing && <ResizeHandles el={displayedElement} b={{ x: 0, y: 0, w: b.w, h: b.h }} frame={layout.frame} scale={scale}
              onDown={onHandleDown} onMove={onHandleMove} onUp={onHandleUp} onCancel={onCancelGesture} />}
          </div>}
        </>
      );
    }

    case 'arrow':
    case 'rect':
    case 'ellipse':
    case 'text':
    case 'stamp':
    case 'image':
    case 'patch': {
      const layout = elementLayout!;
      const fontStyle = standardFontStyle(layout.font);
      const hitTop = el.type === 'arrow' ? Math.min(0, layout.box.y) : 0;
      return <>
        <svg aria-hidden="true" data-slide-artwork={el.id} className="pointer-events-none absolute"
          viewBox={`0 0 ${pageWidth} ${pageHeight}`} width={pageWidth * scale} height={pageHeight * scale}
          style={{ left: 0, top: 0, zIndex: z, overflow: 'hidden' }}>
          <g transform={`translate(${b.x} ${b.y}) rotate(${layout.rotation} ${b.w / 2} ${b.h / 2})`}>
            {layout.path && <path d={layout.path} fill={layout.fill ?? 'none'} fillOpacity={layout.fillOpacity}
              stroke={layout.border ?? 'none'} strokeWidth={layout.strokeWidth} strokeOpacity={layout.opacity}
              strokeLinecap={el.type === 'arrow' ? 'round' : 'butt'} />}
            {imageUrl && <image href={imageUrl} width={b.w} height={b.h} preserveAspectRatio="none" opacity={layout.opacity} onError={() => setFailedImageUrl(imageUrl)} onLoad={() => setFailedImageUrl(null)} />}
            {imageFailed && <text x={4} y={14} fontSize={12} fill="#B91C1C">Image unavailable. Replace it or retry.</text>}
            {!isEditing && layout.texts.map((line, index) => line.text && <text key={index} x={line.x} y={line.y}
              fontSize={layout.size} style={fontStyle} fill={layout.textColor} fillOpacity={layout.opacity}
              textLength={line.width} lengthAdjust="spacingAndGlyphs">{line.text}</text>)}
          </g>
        </svg>
        <div {...handlers} onDoubleClick={el.type === 'image' ? undefined : startEdit} data-slide-element={el.id}
          role="group" aria-label={isEditing ? undefined : `${el.type}: ${el.type === 'image' ? (imageFailed ? 'Image unavailable' : 'Image') : el.text ?? ''}`}
          data-element-overflow={layout.overflow || undefined} className="absolute"
          style={{ left: b.x * scale, top: b.y * scale, width: Math.max(1, b.w * scale), height: Math.max(1, b.h * scale),
            zIndex: z, transform: `rotate(${layout.rotation}deg)`, transformOrigin: 'center',
            cursor: tool === 'select' ? 'move' : undefined }}>
          {el.type === 'arrow' && <div className="absolute" style={{ left: -6, top: hitTop * scale - 6,
            width: Math.max(b.w, layout.box.w) * scale + 12, height: (b.h - hitTop) * scale + 12 }} />}
          {isEditing && !layout.overflow && el.type !== 'image' && <InlineTextEditor
            initial={el.text ?? ''} selectOnMount={selectOnMount} multiline className="absolute"
            placeholder="Type something" onCommit={commit}
            style={{ ...fontStyle, color: layout.textColor, fontSize: layout.size * scale, textAlign: layout.align,
              lineHeight: layout.lineHeight / layout.size, background: '#fff', overflow: 'auto',
              left: layout.box.x * scale, top: layout.box.y * scale,
              width: Math.max(40, layout.box.w * scale), height: Math.max(48, Math.min(layout.box.h, pageHeight * 0.6) * scale) }} />}
        </div>
        {selectionOutline}
      </>;
    }
    case 'block': {
      const art = blockArtwork!;
      const open = isEditing ? art.fields.find(f => f.key === blockField) : undefined;
      return <>
        <svg aria-hidden="true" data-slide-artwork={el.id} className="pointer-events-none absolute"
          viewBox={`0 0 ${pageWidth} ${pageHeight}`} width={pageWidth * scale} height={pageHeight * scale}
          style={{ left: 0, top: 0, zIndex: z, overflow: 'hidden' }}>
          <g transform={`translate(${b.x} ${b.y}) rotate(${el.rotation ?? 0} ${b.w / 2} ${b.h / 2})`} opacity={el.opacity ?? 1}>
            <ArtworkShapes art={art} hidden={open?.key} />
          </g>
        </svg>
        <div {...handlers} data-slide-element={el.id} role="group" aria-label={`${BLOCK_LIBRARY[el.block]?.label ?? 'Stamp'}: double-click a cell to edit it`}
          // The drag gesture captures the pointer, so the double-click lands here, not on a cell: find the cell by position.
          onDoubleClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect(), x = (e.clientX - r.left) / scale, y = (e.clientY - r.top) / scale;
            const hit = art.fields.find(f => x >= f.x && x <= f.x + f.w && y >= f.y && y <= f.y + f.h);
            if (hit) { setBlockField(hit.key); startEdit(); }
          }}
          data-element-overflow={art.overflow || undefined} className="absolute"
          style={{ left: b.x * scale, top: b.y * scale, width: Math.max(1, b.w * scale), height: Math.max(1, b.h * scale),
            zIndex: z, transform: `rotate(${el.rotation ?? 0}deg)`, transformOrigin: 'center', cursor: tool === 'select' ? 'move' : undefined }}>
          {open && <InlineTextEditor key={open.key} initial={open.value} multiline className="absolute" placeholder={open.label}
            onCommit={value => { setBlockField(null); onCommitText(id, value, open.key); }}
            style={{ ...standardFontStyle(open.font), fontSize: open.size * scale, lineHeight: STAMP_LINE_HEIGHT,
              textAlign: open.align, color: '#111111', background: '#fff', overflow: 'auto',
              left: open.x * scale, top: open.y * scale, width: Math.max(40, open.w * scale), height: Math.max(open.size * 2.4, open.h) * scale }} />}
        </div>
        {selectionOutline}
      </>;
    }
    default:
      return null;
  }
});

interface SlidesCanvasProps {
  deck: SlideDeckDTO;
  page: SlidePageDoc;
  /** Eye toggle in the floating toolbar: hides the annotation overlay. */
  showAnnotations?: boolean;
}

const SlidesCanvas = ({
  deck,
  page,
  showAnnotations = true,
}: SlidesCanvasProps) => {
  const pid = deck.projectId;
  const pageIndex = deck.doc.pages.findIndex((candidate) => candidate.id === page.id);
  const chromeZ = page.elements.length + 450;
  const runtime = useSlidesStore((s) => s.runtime[pid]);
  const review = useSlidesStore((s) => s.decks[pid]?.doc?.review);
  const setReview = useSlidesStore((s) => s.setReview);
  const patchRuntime = useSlidesStore((s) => s.patchRuntime);
  const addElement = useSlidesStore((s) => s.addElement);
  const updateElement = useSlidesStore((s) => s.updateElement);
  const removeElement = useSlidesStore((s) => s.removeElement);
  const removeElements = useSlidesStore((s) => s.removeElements);
  const focusPage = useSlidesStore((s) => s.focusPage);

  const tool: SlidesTool = runtime?.activeTool ?? 'select';
  const zoom = runtime?.zoomLevel ?? 1;
  const selectedIds = useMemo(() => runtime?.selectedElementIds ?? [], [runtime?.selectedElementIds]);

  // ── Follow-the-agent UX (Analyst parity) ─────────────────────────────────
  const agentEdits = useSlidesStore((s) => s.agentEdits);
  const autoFollowAgent = useSlidesStore((s) => s.autoFollowAgent);
  const setAutoFollowAgent = useSlidesStore((s) => s.setAutoFollowAgent);
  const clearAgentEdits = useSlidesStore((s) => s.clearAgentEdits);
  const agentBusy = useSlidesSessionBusy();
  const editsForDeck = agentEdits?.projectId === pid;
  // Aura: lights on the agent's first placement, holds through think-gaps
  // while the session is busy, then dissolves (the Analyst phase machine).
  const [auraPhase, setAuraPhase] = useState<'off' | 'on' | 'vanishing'>('off');
  // Re-arms on every placement, including when agentEdits.at changes while
  // editsForDeck stays true, which is what makes the aura hold through a
  // think-gap instead of restarting. It is a phase machine driven by an external
  // store, not derived state, so it cannot be computed during render.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (editsForDeck) setAuraPhase('on');
  }, [editsForDeck, agentEdits?.at]);
  useEffect(() => {
    if (auraPhase !== 'on') return;
    if (agentBusy) return;
    const t = setTimeout(() => setAuraPhase('vanishing'), 5000);
    return () => clearTimeout(t);
  }, [auraPhase, agentBusy, agentEdits?.at]);
  useEffect(() => {
    if (auraPhase !== 'vanishing') return;
    const t = setTimeout(() => setAuraPhase('off'), 640);
    return () => clearTimeout(t);
  }, [auraPhase]);
  // Cursor: pinned on the latest annotation while the agent works; when the
  // loop settles it lifts, blooms and dissolves (same vanish as the Analyst).
  const [cursorVanishing, setCursorVanishing] = useState(false);
  // Same shape as the aura above: a new placement cancels an in-flight vanish.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (editsForDeck) setCursorVanishing(false);
  }, [editsForDeck, agentEdits?.at]);
  useEffect(() => {
    if (!editsForDeck || agentBusy) return;
    const startVanish = setTimeout(() => setCursorVanishing(true), 750);
    const clear = setTimeout(() => clearAgentEdits(), 750 + 620);
    return () => {
      clearTimeout(startVanish);
      clearTimeout(clear);
    };
  }, [editsForDeck, agentEdits?.at, agentBusy, clearAgentEdits]);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** Image tool: where on the page the picked file should land. */
  const imageDropRef = useRef<{ x: number; y: number } | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  // Fit-to-stage scale; zoomLevel is a multiplier on it (1 = fit). The slide
  // maximizes within (stageW - 96, stageH - notesBarH - 96): ~48px side
  // margins (also clearing the vertical Studio toolbar on the left), ~32px
  // top, and reserved room for the handle pill + notes bar.
  const [fitScale, setFitScale] = useState(0.5);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.clientWidth - 96;
      const h = el.clientHeight - 96;
      if (w <= 0 || h <= 0) return;
      setFitScale(Math.max(0.05, Math.min(w / page.width, h / page.height)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [page.width, page.height]);
  const scale = fitScale * zoom;

  // ZOOM AROUND THE POINTER, and drag to pan - the two things that made this
  // canvas feel unlike Studio.
  //
  // Studio zooms with zoomViewportAroundPoint, which keeps whatever is under the
  // cursor fixed. This used to just multiply zoomLevel and leave the scroll
  // position alone, so the drawing slid out from under the pointer on every
  // notch and you had to chase it - the single biggest reason zooming here felt
  // hard to aim. Compensating the scroll offset is all it takes, without moving
  // the module onto a transform-based viewport.
  //
  // Studio also pans by dragging the background. There was no drag-pan here at
  // all, only scrollbars. Middle-button and space+drag do it, rather than
  // Studio's plain left-drag, because in Slides left-drag on the sheet already
  // belongs to the selection and placement tools.
  const spaceHeldRef = useRef(false);
  const panDragRef = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      // Calibrated to Studio's step, but per PIXEL of wheel delta rather than
      // per event. Studio applies a flat 1.4x per wheel event, which is right
      // for a mouse notch and far too much on a trackpad, where one gesture
      // fires a stream of small events and the factor compounds. The exponent
      // is chosen so a full 100px notch lands on exactly Studio's 1.4x while a
      // 10px trackpad nudge gives ~1.03x. Per-event factor is clamped because
      // some mice send deltas of several hundred pixels in one go.
      const cur = useSlidesStore.getState().runtime[pid]?.zoomLevel ?? 1;
      const raw = Math.exp(-e.deltaY * ZOOM_PER_WHEEL_PX);
      const factor = Math.min(WHEEL_ZOOM_FACTOR, Math.max(1 / WHEEL_ZOOM_FACTOR, raw));
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, cur * factor));
      if (next === cur) return;
      // Anchor on the PAGE, not on the scroll content. The sheet is centred
      // (m-auto) inside a padded flex container, so it does not scale about the
      // scroller's origin: when the page is narrower than the viewport the
      // centring offset moves too, and compensating with scrollLeft * ratio
      // left a measured 5-12% drift. Working in page-relative fractions is
      // immune to all of that.
      const wrap = wrapRef.current;
      if (!wrap) {
        patchRuntime(pid, { zoomLevel: next });
        return;
      }
      const before = wrap.getBoundingClientRect();
      const fx = (e.clientX - before.left) / (before.width || 1);
      const fy = (e.clientY - before.top) / (before.height || 1);
      patchRuntime(pid, { zoomLevel: next });
      // The new layout only exists after the re-render, so measure then and
      // shift the scroller by however far the anchor point actually moved.
      requestAnimationFrame(() => {
        const after = wrap.getBoundingClientRect();
        el.scrollLeft += after.left + fx * after.width - e.clientX;
        el.scrollTop += after.top + fy * after.height - e.clientY;
      });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !spaceHeldRef.current) {
        const t = e.target as HTMLElement | null;
        // Not while typing into a note or the composer.
        if (t && /input|textarea/i.test(t.tagName)) return;
        spaceHeldRef.current = true;
        el.style.cursor = 'grab';
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = false;
        el.style.cursor = '';
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [pid, patchRuntime]);

  const onPanPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return false;
    if (e.button !== 1 && !(e.button === 0 && spaceHeldRef.current)) return false;
    e.preventDefault();
    panDragRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
    return true;
  };
  const onPanPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = panDragRef.current;
    const el = scrollRef.current;
    if (!drag || !el || drag.pointerId !== e.pointerId) return false;
    el.scrollLeft = drag.left - (e.clientX - drag.x);
    el.scrollTop = drag.top - (e.clientY - drag.y);
    return true;
  };
  const onPanPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = panDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return false;
    panDragRef.current = null;
    if (scrollRef.current) scrollRef.current.style.cursor = spaceHeldRef.current ? 'grab' : '';
    return true;
  };

  // Interaction state.
  const dragRef = useRef<DragState | null>(null);
  const [dragFrame, setDragFrame] = useState<{ id: string; frame: SlideElementFrame } | null>(null);
  const draftRef = useRef<{ tool: 'arrow' | 'rect' | 'ellipse'; sx: number; sy: number } | null>(null);
  const [draft, setDraft] = useState<{ tool: 'arrow' | 'rect' | 'ellipse'; frame: SlideElementFrame } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const author = useSlidesAuthor();
  const [editingIsNew, setEditingIsNew] = useState(false);
  const overflowReturnFocus = useRef<HTMLButtonElement | null>(null);
  const overflowingNotes = useMemo(() => page.elements.filter((element): element is Exclude<SlideElement, { type: 'image' | 'block' }> =>
    element.type !== 'image' && element.type !== 'block' && (element.type === 'note' ? buildNoteLayout(element, page.width, page.height).overflow : buildElementLayout(element).overflow)), [page]);
  const overflowEditor = overflowingNotes.find(note => note.id === editingId);
  /** Live resize (drag a selection handle). `handle` is a corner/edge key, or
   *  'p0'/'p1' for an arrow's two endpoints. */
  const resizeRef = useRef<{
    id: string;
    handle: ResizeHandle;
    startX: number;
    startY: number;
    frame: SlideElementFrame;
    rotation: number;
  } | null>(null);
  const [resizeFrame, setResizeFrame] = useState<{ id: string; frame: SlideElementFrame } | null>(null);
  const [anchorLive, setAnchorLive] = useState<{ id: string; anchor: { x: number; y: number } } | null>(null);
  const clearAnchorDrag = useRef<(() => void) | null>(null);
  const [stampLive, setStampLive] = useState<SlideElementFrame | null>(null);
  const stampMoveRef = useRef<{ pointerId: number; startX: number; startY: number; frame: SlideElementFrame } | null>(null);
  const stampResizeRef = useRef<{ handle: ResizeHandle; startX: number; startY: number; frame: SlideElementFrame } | null>(null);
  const cancelElementGesture = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
    setDragFrame(null);
    setResizeFrame(null);
    clearAnchorDrag.current?.();
    setAnchorLive(null);
    stampMoveRef.current = null;
    stampResizeRef.current = null;
    setStampLive(null);
  }, []);
  useEffect(() => () => clearAnchorDrag.current?.(), [pid, page.id]);

  // Keyboard: Escape deselects (and drops back to select), Delete removes,
  // ←/→ navigate pages (skipped while an inline editor / input is focused).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.isContentEditable)) return;
      if (e.key === 'Escape') {
        cancelElementGesture();
        patchRuntime(pid, { selectedElementIds: [], activeTool: 'select' });
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        // One mutation for the whole selection, not one per element: that is one
        // save and, since undo records a snapshot per mutation, ONE Ctrl+Z to
        // bring back everything a single Delete removed.
        removeElements(pid, page.id, selectedIds);
        patchRuntime(pid, { selectedElementIds: [] });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const pages = deck.doc.pages;
        const idx = pages.findIndex((p) => p.id === page.id);
        const next = pages[idx + (e.key === 'ArrowRight' ? 1 : -1)];
        if (next) {
          e.preventDefault();
          focusPage(pid, next.id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pid, page.id, deck, selectedIds, patchRuntime, removeElements, focusPage]);

  const toPoint = (e: React.PointerEvent): { x: number; y: number } => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
  };

  const commitInlineText = useCallback(
    (id: string, raw: string, field?: string) => {
      setEditingId(null);
      setEditingIsNew(false);
      const existing = useSlidesStore.getState().decks[pid]?.doc.pages.find(p => p.id === page.id)?.elements.find(el => el.id === id);
      if (!existing || existing.type === 'image') return;
      if (existing.type === 'block') {
        if (field && raw !== (existing.fields[field] ?? '')) updateElement(pid, page.id, id, { fields: { ...existing.fields, [field]: raw } }, { coalesce: false });
        return;
      }
      if (raw === (existing.text ?? '') && (!editingIsNew || raw.length > 0)) return;
      const text = raw;
      if (!text && (existing.type === 'note' || existing.type === 'text')) {
        removeElement(pid, page.id, id);
        patchRuntime(pid, { selectedElementIds: [] });
      } else {
        updateElement(pid, page.id, id, existing.type === 'note' && author ? { text, writtenBy: author } : { text }, { coalesce: false });
      }
    },
    [pid, page.id, editingIsNew, removeElement, patchRuntime, updateElement, author]
  );

  const startEditElement = useCallback((id: string) => {
    setEditingIsNew(false);
    setEditingId(id);
  }, []);

  // Move / resize the validation stamp overlay. Box is in fractions of page
  // width; kept in local state during the drag (smooth) then committed to
  // review.stamp on release so it persists and the exporter matches.
  // The stamp is a frame in page POINTS (x,y,w,h), like a slide element - so it
  // reuses the element resize math (resizeFrameBy) and the 8-handle chrome.
  const stampFrame: SlideElementFrame = useMemo(
    () => resolveStampFrame(review?.stamp, page.width),
    [review?.stamp, page.width]
  );
  useEffect(() => () => {
    stampMoveRef.current = null;
    stampResizeRef.current = null;
    setStampLive(null);
  }, [pid, page.id]);
  // Identity-stable geometry for the stamp's (memoized) resize chrome.
  const stampNormFrame = useMemo(() => normFrame(stampLive ?? stampFrame), [stampLive, stampFrame]);
  const cardLayout = useMemo(() => review && pageIndex === 0
    ? buildStampCardLayout(review, stampNormFrame.w, stampNormFrame.h, review.stamp?.fontScale ?? 1, deck.reviewRound, deck.name)
    : null, [review, pageIndex, stampNormFrame, deck.reviewRound, deck.name]);
  const cardArtwork = useMemo(() => review && cardLayout ? buildStampArtwork(review, stampNormFrame.w, cardLayout, stampNormFrame.h) : undefined,
    [review, cardLayout, stampNormFrame.w, stampNormFrame.h]);
  const stampOverflow = cardArtwork?.overflow ?? false;
  const stampSynthEl = useMemo(
    () => ({ id: '__stamp__', type: 'rect', frame: stampNormFrame }) as unknown as SlideElement,
    [stampNormFrame]
  );

  // RESIZE: 8 handles, forked from the element resize handlers (writes to review.stamp).
  const onStampHandleDown = useCallback((e: React.PointerEvent, _el: SlideElement, handle: ResizeHandle) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    stampResizeRef.current = { handle, startX: e.clientX, startY: e.clientY, frame: stampFrame };
    setStampLive(stampFrame);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [stampFrame]);
  const onStampHandleMove = useCallback((e: React.PointerEvent) => {
    const r = stampResizeRef.current;
    if (!r) return;
    setStampLive(resizeFrameBy(r.frame, r.handle, (e.clientX - r.startX) / scale, (e.clientY - r.startY) / scale, e.shiftKey));
  }, [scale]);
  const onStampHandleUp = useCallback((e: React.PointerEvent) => {
    const r = stampResizeRef.current;
    stampResizeRef.current = null;
    setStampLive(null);
    if (!r) return;
    const live = resizeFrameBy(r.frame, r.handle, (e.clientX - r.startX) / scale, (e.clientY - r.startY) / scale, e.shiftKey);
    const next = normFrame(live);
    if (next.w < 40 || next.h < 40) return;
    if (next.x === r.frame.x && next.y === r.frame.y && next.w === r.frame.w && next.h === r.frame.h) return;
    // normFrame drops any extra props, so carry the text-size preference across a
    // resize (setReview overwrites `stamp` wholesale - otherwise a resize would
    // silently reset fontScale to the default).
    const fontScale = useSlidesStore.getState().decks[pid]?.doc.review?.stamp?.fontScale ?? 1;
    setReview(pid, { stamp: { ...next, fontScale } });
  }, [pid, scale, setReview]);
  const onStampHandleCancel = useCallback(() => {
    stampResizeRef.current = null;
    setStampLive(null);
  }, []);

  const startStampMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (stampMoveRef.current) return;
    e.preventDefault();
    // End inline editing so Ctrl+Z after the drag reaches deck history.
    e.currentTarget.focus({ preventScroll: true });
    stampMoveRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, frame: stampFrame };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onStampMove = (e: React.PointerEvent) => {
    const move = stampMoveRef.current;
    if (move && move.pointerId === e.pointerId) setStampLive({ ...move.frame, x: move.frame.x + (e.clientX - move.startX) / scale, y: move.frame.y + (e.clientY - move.startY) / scale });
  };
  const cancelStampMove = (e?: React.PointerEvent) => {
    if (e && stampMoveRef.current?.pointerId !== e.pointerId) return;
    stampMoveRef.current = null;
    setStampLive(null);
  };
  const finishStampMove = (e: React.PointerEvent) => {
    const move = stampMoveRef.current;
    if (!move || move.pointerId !== e.pointerId) return;
    cancelStampMove();
    if (e.clientX === move.startX && e.clientY === move.startY) return;
    const fontScale = useSlidesStore.getState().decks[pid]?.doc.review?.stamp?.fontScale ?? 1;
    setReview(pid, { stamp: { ...move.frame, x: move.frame.x + (e.clientX - move.startX) / scale, y: move.frame.y + (e.clientY - move.startY) / scale, fontScale } });
  };

  // Drag the anchor handle of a note to re-aim its leader line (correct the
  // pointing). Delta-based in page points, like the element move/resize drags.
  const startAnchorDrag = useCallback((e: React.PointerEvent, el: SlideElement) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startAnchor = (el as { anchor?: { x: number; y: number } }).anchor ?? { x: el.frame.x, y: el.frame.y };
    clearAnchorDrag.current?.();
    const point = (ev: PointerEvent) => ({ x: startAnchor.x + (ev.clientX - startX) / scale, y: startAnchor.y + (ev.clientY - startY) / scale });
    const move = (ev: PointerEvent) => {
      setAnchorLive({ id: el.id, anchor: point(ev) });
    };
    const clear = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', clear);
      clearAnchorDrag.current = null;
      setAnchorLive(null);
    };
    const up = (ev: PointerEvent) => {
      clear();
      const anchor = point(ev);
      if (anchor.x !== startAnchor.x || anchor.y !== startAnchor.y) updateElement(pid, page.id, el.id, { anchor } as Partial<SlideElement>, { coalesce: false });
    };
    clearAnchorDrag.current = clear;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', clear);
  }, [pid, page.id, scale, updateElement]);

  const placeAt = (pt: { x: number; y: number }, activeTool: 'note' | 'text' | 'stamp') => {
    const id = slidesGenId();
    if (activeTool === 'note') {
      // The click is the spot commented on: the bubble sits beside it with its arrow already drawn
      // (RDM pilot, 2026-10-12), above and to the right, flipped where the page edge leaves no room.
      const w = 220, h = 72, gap = 40;
      const x = pt.x + gap + w <= page.width ? pt.x + gap : Math.max(0, pt.x - gap - w);
      const y = pt.y - gap - h >= 0 ? pt.y - gap - h : Math.min(page.height - h, pt.y + gap);
      addElement(pid, page.id, { id, type: 'note', text: '', severity: 'info', anchor: { x: pt.x, y: pt.y }, frame: { x, y, w, h } });
    } else if (activeTool === 'text') {
      addElement(pid, page.id, {
        id,
        type: 'text',
        text: '',
        fontSize: 16,
        // Authored slide text is black (TEXT_INK, matching the PDF export's
        // default). DEFAULT_INK is annotation red and was making every
        // hand-placed text box red while agent-placed ones came out black.
        color: TEXT_INK,
        frame: { x: pt.x, y: pt.y, w: 240, h: 48 },
      });
    } else {
      addElement(pid, page.id, {
        id,
        type: 'stamp',
        text: 'STAMP',
        verdict: 'fail',
        frame: { x: pt.x, y: pt.y, w: 120, h: 26 },
      });
    }
    patchRuntime(pid, { selectedElementIds: [id], activeTool: 'select', rightBarTab: 'params' });
    setEditingId(id);
    setEditingIsNew(true);
  };

  /** Image tool: upload the picked file, then place it at the click point,
   *  scaled to fit the page while preserving the source aspect ratio. */
  const placeImageFile = async (file: File) => {
    const drop = imageDropRef.current ?? { x: 0, y: 0 };
    imageDropRef.current = null;
    setUploading(true);
    try {
      const natural = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
          URL.revokeObjectURL(objectUrl);
        };
        img.onerror = () => {
          resolve({ w: 1, h: 1 });
          URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
      });

      const url = await uploadFileToCloud(file, file.name, file.type || 'image/png');
      if (!url) throw new Error('upload failed');

      // Fit inside whatever room is left from the drop point, capped at half
      // the page so a big photo doesn't swallow the slide.
      const maxW = Math.min(page.width * 0.5, Math.max(80, page.width - drop.x));
      const ratio = natural.h / natural.w;
      const w = maxW;
      const h = w * ratio;
      const id = slidesGenId();
      addElement(pid, page.id, {
        id,
        type: 'image',
        url,
        frame: { x: drop.x, y: drop.y, w, h },
      });
      patchRuntime(pid, { selectedElementIds: [id], activeTool: 'select', rightBarTab: 'params' });
    } catch {
      toast.error('Could not add that image.');
    } finally {
      setUploading(false);
    }
  };

  // Copy, cut and paste (RDM pilot, 2026-10-10): bubbles copy as a set, and a pasted screenshot lands
  // on the page like the image tool. A focused text field keeps the browser's own clipboard. Capture
  // phase, so the app-wide Studio paste handler sees the paste already taken.
  const placeImageRef = useRef(placeImageFile);
  useEffect(() => { placeImageRef.current = placeImageFile; });
  useEffect(() => {
    const inField = (t: EventTarget | null) => t instanceof HTMLElement && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.isContentEditable);
    const onCopy = (e: ClipboardEvent) => {
      // Text highlighted anywhere (a chat answer) is what Ctrl+C means, even with a bubble still selected.
      if (inField(e.target) || !selectedIds.length || !e.clipboardData || !window.getSelection()?.isCollapsed) return;
      const els = page.elements.filter((el) => selectedIds.includes(el.id));
      if (!els.length) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', copiedElementsText(els));
      if (e.type === 'cut') {
        removeElements(pid, page.id, els.map((el) => el.id));
        patchRuntime(pid, { selectedElementIds: [] });
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      if (inField(e.target) || !e.clipboardData) return;
      const image = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
      if (image) {
        e.preventDefault();
        imageDropRef.current = { x: page.width * 0.25, y: page.height * 0.25 };
        void placeImageRef.current(image);
        return;
      }
      const els = pastedElements(e.clipboardData.getData('text/plain'), page, slidesGenId);
      if (!els) return;
      e.preventDefault();
      // One mutation, so one Ctrl+Z takes the whole paste back.
      useSlidesStore.getState().applyDocMutation(pid, (doc) => {
        doc.pages.find((p) => p.id === page.id)?.elements.push(...els);
        return doc;
      });
      patchRuntime(pid, { selectedElementIds: els.map((el) => el.id), activeTool: 'select' });
    };
    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCopy, true);
    document.addEventListener('paste', onPaste, true);
    return () => {
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('cut', onCopy, true);
      document.removeEventListener('paste', onPaste, true);
    };
  }, [pid, page, selectedIds, patchRuntime, removeElements]);

  // --- Page-surface pointer handlers (placement + background deselect) -------
  const onSurfacePointerDown = (e: React.PointerEvent) => {
    // Panning wins over every tool: a middle-drag or space-drag is a viewport
    // gesture, not a drawing one.
    if (onPanPointerDown(e as React.PointerEvent<HTMLDivElement>)) return;
    if (e.button !== 0) return;
    const pt = toPoint(e);
    if (tool === 'select') {
      patchRuntime(pid, { selectedElementIds: [] });
      return;
    }
    if (tool === 'note' || tool === 'text' || tool === 'stamp') {
      placeAt(pt, tool);
      return;
    }
    if (tool === 'image') {
      // Images come from a file, not a drag: remember where to drop it and let
      // the hidden picker take over.
      imageDropRef.current = pt;
      imageInputRef.current?.click();
      patchRuntime(pid, { activeTool: 'select' });
      return;
    }
    // arrow / rect / ellipse: click-drag defines the vector/frame.
    draftRef.current = { tool, sx: pt.x, sy: pt.y };
    setDraft({ tool, frame: { x: pt.x, y: pt.y, w: 0, h: 0 } });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onSurfacePointerMove = (e: React.PointerEvent) => {
    if (onPanPointerMove(e as React.PointerEvent<HTMLDivElement>)) return;
    const d = draftRef.current;
    if (!d) return;
    const pt = toPoint(e);
    setDraft({ tool: d.tool, frame: { x: d.sx, y: d.sy, w: pt.x - d.sx, h: pt.y - d.sy } });
  };

  const onSurfacePointerUp = (e: React.PointerEvent) => {
    if (onPanPointerUp(e as React.PointerEvent<HTMLDivElement>)) return;
    const d = draftRef.current;
    if (!d) return;
    draftRef.current = null;
    setDraft(null);
    const pt = toPoint(e);
    const frame: SlideElementFrame = { x: d.sx, y: d.sy, w: pt.x - d.sx, h: pt.y - d.sy };
    if (Math.abs(frame.w) < 4 && Math.abs(frame.h) < 4) {
      patchRuntime(pid, { activeTool: 'select' });
      return;
    }
    const id = slidesGenId();
    if (d.tool === 'arrow') {
      // Arrows keep the signed vector (origin → origin + w/h).
      addElement(pid, page.id, { id, type: 'arrow', color: DEFAULT_INK, strokeWidth: 2, frame });
    } else {
      addElement(pid, page.id, {
        id,
        type: d.tool === 'ellipse' ? 'ellipse' : 'rect',
        color: DEFAULT_INK,
        strokeWidth: 2,
        frame: normFrame(frame),
      });
    }
    patchRuntime(pid, { selectedElementIds: [id], activeTool: 'select', rightBarTab: 'params' });
  };

  // --- Element pointer handlers (select + drag-move) -------------------------
  // These are handed to every memoized element, so their identity has to hold
  // still through the hot paths. The tool and the selection are read from the
  // store AT EVENT TIME (the same derivation the render does, so the value is
  // identical) rather than captured as deps; `isEditing` is passed in by the
  // element that already knows it. `scale` stays a real dependency: it cannot
  // move during a drag, and when it does move (zoom) every element re-renders
  // anyway because its geometry changed.
  const onElementPointerDown = useCallback(
    (e: React.PointerEvent, el: SlideElement, isEditing: boolean) => {
      const rt = useSlidesStore.getState().runtime[pid];
      if ((rt?.activeTool ?? 'select') !== 'select' || e.button !== 0) return;
      e.stopPropagation();
      if (!(rt?.selectedElementIds ?? []).includes(el.id)) {
        patchRuntime(pid, { selectedElementIds: [el.id], rightBarTab: 'params' });
      }
      if (isEditing) return;
      dragRef.current = { id: el.id, startX: e.clientX, startY: e.clientY, frame: el.frame, moved: false };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [pid, patchRuntime]
  );
  const onElementPointerMove = useCallback(
    (e: React.PointerEvent, id: string) => {
      const d = dragRef.current;
      if (!d || d.id !== id) return;
      if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 3) return;
      d.moved = true;
      setDragFrame({ id, frame: { ...d.frame, x: d.frame.x + (e.clientX - d.startX) / scale, y: d.frame.y + (e.clientY - d.startY) / scale } });
    },
    [scale]
  );
  const onElementPointerUp = useCallback(
    (e: React.PointerEvent, id: string) => {
      const d = dragRef.current;
      if (!d || d.id !== id) return;
      dragRef.current = null;
      setDragFrame(null);
      if (d.moved) {
        const dx = (e.clientX - d.startX) / scale;
        const dy = (e.clientY - d.startY) / scale;
        if (dx === 0 && dy === 0) return;
        updateElement(pid, page.id, id, {
          frame: { ...d.frame, x: d.frame.x + dx, y: d.frame.y + dy },
        }, { coalesce: false });
      }
    },
    [pid, page.id, scale, updateElement]
  );

  const effectiveFrame = (el: SlideElement): SlideElementFrame => {
    if (resizeFrame && resizeFrame.id === el.id) return resizeFrame.frame;
    if (dragFrame && dragFrame.id === el.id) return dragFrame.frame;
    return el.frame;
  };

  // --- Resize (drag a selection handle) --------------------------------------
  const onHandlePointerDown = useCallback((e: React.PointerEvent, el: SlideElement, handle: ResizeHandle) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    resizeRef.current = { id: el.id, handle, startX: e.clientX, startY: e.clientY, frame: el.frame, rotation: el.rotation ?? 0 };
    setResizeFrame({ id: el.id, frame: el.frame });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onHandlePointerMove = useCallback((e: React.PointerEvent) => {
    const r = resizeRef.current;
    if (!r) return;
    const dx = (e.clientX - r.startX) / scale;
    const dy = (e.clientY - r.startY) / scale;
    const next = resizeFrameBy(r.frame, r.handle, dx, dy, e.shiftKey, r.rotation);
    setResizeFrame({ id: r.id, frame: next });
  }, [scale]);

  const onHandlePointerUp = useCallback((e: React.PointerEvent) => {
    const r = resizeRef.current;
    resizeRef.current = null;
    setResizeFrame(null);
    if (!r || (e.clientX === r.startX && e.clientY === r.startY)) return;
    const live = resizeFrameBy(r.frame, r.handle, (e.clientX - r.startX) / scale, (e.clientY - r.startY) / scale, e.shiftKey, r.rotation);
    // Arrows keep their signed vector; everything else is normalized so a
    // handle dragged past the opposite edge flips cleanly instead of going
    // negative-width.
    const isArrow = r.handle === 'p0' || r.handle === 'p1';
    const next = isArrow ? live : normFrame(live);
    if (!isArrow && (next.w < 1 || next.h < 1)) return;
    const start = isArrow ? r.frame : normFrame(r.frame);
    if (next.x === start.x && next.y === start.y && next.w === start.w && next.h === start.h) return;
    updateElement(pid, page.id, r.id, { frame: next }, { coalesce: false });
  }, [pid, page.id, scale, updateElement]);

  // How much of each just-placed callout is written so far. `undefined` for a
  // note that is not being typed, which is the normal case and renders in full.
  const typingShown = useSlidesStore((s) => s.agentTyping.shown);

  // Type the queue head out a character at a time, then hand the note back to
  // the normal render path and start the next.
  const advanceAgentTyping = useSlidesStore((s) => s.advanceAgentTyping);
  const clearAgentTyping = useSlidesStore((s) => s.clearAgentTyping);
  // "The agent is looking at this sheet." Reading a page takes real seconds and
  // produces nothing on screen, which reads as a stall; a slow pulse says the
  // tool is busy on THIS page. Gated on the session actually being busy so it
  // cannot be left pulsing after the turn ends.
  const readingPageId = useSlidesStore((s) => s.readingPageId);
  const isBeingRead = agentBusy && !!page && readingPageId === page.id;
  const typingHeadId = useSlidesStore((s) => s.agentTyping.order[0]);
  const typingQueue = useSlidesStore((s) => s.agentTyping.order);
  const typingHead = typingHeadId;
  // The agent's outline sits on the callout being WRITTEN, from its first character (owner,
  // 2026-10-06). agentEdits marks the last element of a placed batch, which is still queued and
  // empty while an earlier callout is typed, so it only leads when nothing is being typed.
  const cursorFrame = useMemo(() => {
    const head = typingHeadId ? page?.elements.find((e) => e.id === typingHeadId) : undefined;
    if (head) return (typingShown[head.id] ?? 0) > 0 ? head.frame : null;
    if (!agentEdits || !editsForDeck || agentEdits.pageId !== page?.id) return null;
    return agentEdits.elementId && typingQueue.includes(agentEdits.elementId) ? null : agentEdits.frame;
  }, [typingHeadId, typingShown, typingQueue, page, agentEdits, editsForDeck]);
  const typingFullText = useMemo(() => {
    if (!typingHeadId) return null;
    const el = page?.elements.find((e) => e.id === typingHeadId);
    if (!el) return null;
    return 'text' in el ? ((el.text as string | undefined) ?? '') : '';
  }, [typingHeadId, page]);

  // ZOOM TO THE CALLOUT WHILE IT IS BEING WRITTEN, then back out.
  //
  // The writing is the moment worth watching, and at fit zoom an A3 sheet
  // renders a comment at ~5px - legible only in principle. This frames the
  // bubble AND the object it points at, holds while the text is typed, and
  // restores the previous view once the queue is empty, so the reviewer is not
  // left somewhere they did not choose to be.
  const zoomRestoreRef = useRef<{ zoom: number; left: number; top: number } | null>(null);
  const zoomAnimRef = useRef<number | null>(null);
  useEffect(() => {
    const container = scrollRef.current;
    const pid = deck?.projectId;
    if (!container || !pid || !page) return;
    const reduced =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const animateTo = (targetZoom: number, centre: { x: number; y: number } | null) => {
      if (zoomAnimRef.current !== null) cancelAnimationFrame(zoomAnimRef.current);
      const startZoom = useSlidesStore.getState().runtime[pid]?.zoomLevel ?? 1;
      const startedAt = performance.now();
      const DURATION = 320;
      const frame = (now: number) => {
        const t = Math.min(1, (now - startedAt) / DURATION);
        // ease-in-out: the motion should feel deliberate, not mechanical.
        const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
        const z = startZoom + (targetZoom - startZoom) * e;
        patchRuntime(pid, { zoomLevel: z });
        if (centre) {
          const sc = fitScale * z;
          container.scrollLeft = centre.x * sc - container.clientWidth / 2;
          container.scrollTop = centre.y * sc - container.clientHeight / 2;
        }
        if (t < 1) zoomAnimRef.current = requestAnimationFrame(frame);
        else zoomAnimRef.current = null;
      };
      zoomAnimRef.current = requestAnimationFrame(frame);
    };

    if (typingHead) {
      const el = page.elements.find((e) => e.id === typingHead);
      if (!el) return;
      const a = (el as { anchor?: { x: number; y: number } }).anchor;
      // Frame the bubble together with the thing it points at: showing one
      // without the other is exactly the context a reviewer needs.
      const x0 = Math.min(el.frame.x, a?.x ?? el.frame.x);
      const y0 = Math.min(el.frame.y, a?.y ?? el.frame.y);
      const x1 = Math.max(el.frame.x + el.frame.w, a?.x ?? el.frame.x + el.frame.w);
      const y1 = Math.max(el.frame.y + el.frame.h, a?.y ?? el.frame.y + el.frame.h);
      const PAD = 40;
      const w = x1 - x0 + PAD * 2;
      const h = y1 - y0 + PAD * 2;
      if (!zoomRestoreRef.current) {
        zoomRestoreRef.current = {
          zoom: useSlidesStore.getState().runtime[pid]?.zoomLevel ?? 1,
          left: container.scrollLeft,
          top: container.scrollTop,
        };
      }
      // GO IN PROPERLY. Fitting the bubble and its target into the viewport
      // sounds right and is not: on a sheet where the comment sits in the margin
      // and the object is across the page, the union is most of the sheet, so
      // "fit the union" resolved to ~1.4x - barely a zoom at all, which is what
      // the reviewer saw. Floor it, and when the pair genuinely cannot both fit
      // at that depth, keep the BUBBLE centred: the writing is the thing being
      // watched, and the leader line already shows which way the object lies.
      const wanted = Math.min(
        container.clientWidth / (w * fitScale),
        container.clientHeight / (h * fitScale)
      );
      const target = Math.max(MIN_WRITE_ZOOM, Math.min(ZOOM_MAX, MAX_WRITE_ZOOM, wanted));
      const bothFit =
        w * fitScale * target <= container.clientWidth &&
        h * fitScale * target <= container.clientHeight;
      const centre = bothFit
        ? { x: (x0 + x1) / 2, y: (y0 + y1) / 2 }
        : { x: el.frame.x + el.frame.w / 2, y: el.frame.y + el.frame.h / 2 };
      animateTo(target, centre);
      return;
    }
    // Queue drained: put the view back exactly where the reviewer had it.
    const restore = zoomRestoreRef.current;
    if (restore) {
      zoomRestoreRef.current = null;
      animateTo(restore.zoom, null);
      window.setTimeout(() => {
        container.scrollTo({ left: restore.left, top: restore.top, behavior: 'smooth' });
      }, 340);
    }
  }, [typingHead, page, deck?.projectId, fitScale, patchRuntime]);

  useEffect(() => {
    if (!typingHead) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      clearAgentTyping();
      return;
    }
    if (typingFullText === null) {
      // Not on this page (the agent moved on): let it go rather than holding a
      // note invisible forever.
      advanceAgentTyping(typingHead, 0, true);
      return;
    }
    const full = typingFullText;
    if (full.length === 0) {
      advanceAgentTyping(typingHead, 0, true);
      return;
    }
    // HUMAN PACE, one character at a time. The first cut typed a whole comment
    // in a fixed ~1.8s regardless of length, which for a 120-character finding
    // is ~67 characters a second - a blur, not writing. It also advanced in
    // multi-character steps, which reads as a progress bar rather than a hand.
    //
    // ~18 characters a second is a brisk typist: a 120-character comment takes
    // about 7 seconds, which is long enough to watch and short enough to sit
    // through several. The jitter matters more than the mean - perfectly even
    // keystrokes are the thing that reads as a machine - and the pause after
    // sentence-ending punctuation is what a person does while thinking.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (cancelled) return;
      const shown = useSlidesStore.getState().agentTyping.shown[typingHead];
      if (shown === undefined) return;
      const next = shown + 1;
      if (next >= full.length) {
        advanceAgentTyping(typingHead, full.length, true);
        return;
      }
      advanceAgentTyping(typingHead, next, false);
      const justTyped = full[next - 1] ?? '';
      const base = MS_PER_CHAR * (0.6 + Math.random() * 0.8);
      const pause = /[.!?]/.test(justTyped) ? SENTENCE_PAUSE_MS : /[,;:]/.test(justTyped) ? CLAUSE_PAUSE_MS : 0;
      timer = setTimeout(tick, base + pause);
    };
    timer = setTimeout(tick, MS_PER_CHAR);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Deps are the TEXT, not the page object. The deck doc is cloned on every
    // mutation, so depending on `page` tore down and rebuilt this interval on
    // each note added - during a burst of adds the 26ms tick barely ever fired,
    // and the typing only got going once the document went quiet, by which time
    // the agent had moved on. A string dep is stable across those clones.
  }, [typingHead, typingFullText, advanceAgentTyping, clearAgentTyping]);

  return (
    <div ref={stageRef} className="group relative flex min-h-0 flex-1 flex-col bg-black">
      {(stampOverflow || overflowingNotes.length > 0) && <div
        className="absolute bottom-16 left-3 z-[460] max-h-[calc(100%-12rem)] w-[calc(100%-1.5rem)] max-w-sm overflow-auto rounded-xl border border-[#3A3A3A] bg-[#222] p-3 text-sm text-[#F3F4F6]">
      {stampOverflow && <div role="alert" data-stamp-overflow-warning>
        <p>{STAMP_OVERFLOW_MESSAGE}</p>
        <button type="button" className="mt-2 rounded-lg bg-[#333] px-3 py-2"
          onClick={() => patchRuntime(pid, { selectedElementIds: ['__stamp__'], rightBarTab: 'params' })}>Select validation card</button>
      </div>}
      {overflowingNotes.length > 0 && <div className={stampOverflow ? 'mt-3' : undefined}>
        <p>{overflowingNotes.every(element => element.type === 'note') ? NOTE_OVERFLOW_MESSAGE : ELEMENT_OVERFLOW_MESSAGE}</p>
        {overflowingNotes.map((note, index) => <button key={note.id} type="button" data-note-overflow-edit={note.type === 'note' ? note.id : undefined} data-element-overflow-edit={note.id}
          className="mt-2 block w-full rounded-lg bg-[#333] px-3 py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#B3D9FF]"
          onClick={event => { overflowReturnFocus.current = event.currentTarget; patchRuntime(pid, { selectedElementIds: [note.id], rightBarTab: 'params' }); startEditElement(note.id); }}>
          {note.type === 'note' ? 'Comment' : 'Text'} {index + 1} does not fit · Edit: {note.text?.slice(0, 70) || 'Empty text'}
        </button>)}
        {overflowEditor && <InlineTextEditor key={overflowEditor.id} initial={overflowEditor.text ?? ''} multiline
          className="mt-3 h-36 w-full" placeholderClassName="placeholder:text-[#94A3B8]"
          onCommit={value => {
            commitInlineText(overflowEditor.id, value);
            requestAnimationFrame(() => overflowReturnFocus.current?.focus());
          }} />}
      </div>}
      </div>}
      {/* "Agent working" aura - the Analyst's pulsing gradient border, over the
          whole stage while the Reviewer annotates the deck. */}
      <div
        aria-hidden
        style={{ zIndex: 45 }}
        className={`${agentFx.agentGlow} ${auraPhase === 'on' ? agentFx.agentGlowOn : auraPhase === 'vanishing' ? agentFx.agentGlowVanish : ''}`}
      />
      {/* Follow chip: the view is riding the agent's cursor - click to detach. */}
      {autoFollowAgent && agentBusy && editsForDeck && (
        <button
          type="button"
          onClick={() => setAutoFollowAgent(false)}
          className="absolute left-1/2 top-2 z-[46] flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-[#2E5BA6] bg-[#161B24] px-3 py-1 text-[12px] text-[#BFD7FF] shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-colors hover:bg-[#1B2330]"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3B82F6] opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#3B82F6]" />
          </span>
          Following agent · Stop
        </button>
      )}
      {!autoFollowAgent && agentBusy && (
        <button
          type="button"
          onClick={() => setAutoFollowAgent(true)}
          className="absolute left-1/2 top-2 z-[46] flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/[0.06] bg-[#353535] px-3 py-1 text-[12px] text-white/60 shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-colors hover:bg-[#222] hover:text-white/85"
        >
          Follow agent
        </button>
      )}
      {/* Image tool's file picker - opened by clicking the page with the image
          tool active, so the click point becomes the drop point. */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void placeImageFile(file);
        }}
      />
      {uploading && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-md bg-white/10 px-2.5 py-1 text-[11px] text-white/80 backdrop-blur">
          Adding image…
        </div>
      )}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto">
        <div className="flex min-h-full w-max min-w-full px-12 pb-2 pt-8">
          <div className="relative m-auto shrink-0">
            <div
              ref={wrapRef}
              className={`relative select-none rounded-lg shadow-xl ring-1 ring-white/10 ${
                isBeingRead ? slidesFx.pageBeingRead : ''
              }`}
              style={{
                isolation: 'isolate',
                width: page.width * scale,
                height: page.height * scale,
                cursor: tool === 'select' ? 'default' : 'crosshair',
              }}
              onPointerDown={onSurfacePointerDown}
              onPointerMove={onSurfacePointerMove}
              onPointerUp={onSurfacePointerUp}
            >
              {/* Immutable base (PDF snapshot / white sheet), clipped to the card. */}
              <div
                className="absolute inset-0 overflow-hidden rounded-lg"
                style={{ backgroundColor: page.background ?? '#FFFFFF' }}
              >
                {page.snapshotUrl && (
                   
                  <img
                    src={page.snapshotUrl}
                    alt={page.label ?? `Page ${page.index + 1}`}
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full select-none"
                  />
                )}
              </div>

              {/* Validation stamp - visible + editable on page 1. MOVE by dragging
                  the header band; RESIZE (all dimensions) with the 8 handles, forked
                  from the element resize chrome. Both persist to review.stamp (a
                  page-point frame) and the exporter reads the same frame. */}
              {showAnnotations && pageIndex === 0 && review && cardLayout && (() => {
                const nf = stampNormFrame;
                const left = nf.x * scale, top = nf.y * scale, w = nf.w * scale, h = nf.h * scale;
                const synthEl = stampSynthEl;
                const fontScale = review.stamp?.fontScale ?? 1;
                // The grip must cover the header band and NOTHING else, or it
                // swallows clicks meant for the editable header fields and the
                // first rows underneath it. The band's height depends on the
                // FITTED font, not on the box width, so take it from the same
                // layout the card renders with rather than from a ratio (a
                // hardcoded w * 0.122 covered a third of the card once the fit
                // shrank the font for long comments).
                const headerH = cardLayout.headerH * scale;
                const selectStamp = () => patchRuntime(pid, { selectedElementIds: ['__stamp__'], rightBarTab: 'params' });
                const selected = !!runtime?.selectedElementIds?.includes('__stamp__');
                return (
                  <React.Fragment key="__stamp__">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: chromeZ }}>
                    <div
                      className="absolute"
                      style={{ left, top, width: w, height: h, pointerEvents: 'auto',
                        outline: selected ? '1.5px solid #0033A0' : undefined }}
                      onClick={selectStamp}
                    >
                      <div style={{ width: nf.w, height: nf.h, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                        <SlidesValidationCard projectId={pid} review={review} width={nf.w} height={nf.h} fontScale={fontScale} layout={cardLayout} artwork={cardArtwork} />
                      </div>
                      {/* MOVE grip over the (non-editable) header band; also selects. */}
                      <div
                        onPointerDown={(e) => { selectStamp(); startStampMove(e); }}
                        onPointerMove={onStampMove} onPointerUp={finishStampMove}
                        onPointerCancel={cancelStampMove} onLostPointerCapture={cancelStampMove} tabIndex={-1}
                        title="Drag to move the stamp - its settings are in the right bar"
                        className="absolute"
                        style={{ left: 0, top: 0, width: '100%', height: headerH, cursor: 'move', zIndex: 3, touchAction: 'none' }}
                      />
                    </div>
                    </div>
                    {/* RESIZE: the 8-handle box chrome - shown only when the stamp is selected. */}
                    {selected && (
                      <div className="absolute inset-0" style={{ zIndex: chromeZ + 10, pointerEvents: 'none' }}>
                        <div style={{ pointerEvents: 'auto' }}>
                          <ResizeHandles el={synthEl} b={nf} frame={nf} scale={scale} onDown={onStampHandleDown} onMove={onStampHandleMove} onUp={onStampHandleUp} onCancel={onStampHandleCancel} />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })()}

              {/* Overlay element tree (page points → CSS px); Eye-toggleable. */}
              {showAnnotations &&
                page.elements.map((el, paintIndex) => (
                  <SlidesCanvasElement
                    key={el.id}
                    el={anchorLive?.id === el.id ? { ...el, anchor: anchorLive.anchor } as SlideElement : el}
                    frame={effectiveFrame(el)}
                    scale={scale}
                    tool={tool}
                    // Paint order = array order, exactly like the exporter's painter's
                    // algorithm. Per-type z-10/z-20 classes used to override this, so a rect
                    // added after a text box sat UNDER it on canvas but OVER it in the PDF.
                    z={paintIndex + 1}
                    isSelected={selectedIds.includes(el.id)}
                    isEditing={editingId === el.id}
                    selectOnMount={editingId === el.id && editingIsNew}
                    pageWidth={page.width}
                    pageHeight={page.height}
                    typingChars={typingShown[el.id]}
                    onPointerDownEl={onElementPointerDown}
                    onPointerMoveEl={onElementPointerMove}
                    onPointerUpEl={onElementPointerUp}
                    onStartEdit={startEditElement}
                    onCommitText={commitInlineText}
                    onAnchorDown={startAnchorDrag}
                    onHandleDown={onHandlePointerDown}
                    onHandleMove={onHandlePointerMove}
                    onHandleUp={onHandlePointerUp}
                    onCancelGesture={cancelElementGesture}
                  />
                ))}

              {/* Fater agent cursor - the Analyst's glowing pointer, hugging the
                  annotation the Reviewer just placed and gliding to the next one
                  (the CSS transform transition does the glide). Page-space, so it
                  scales/pans with the slide. */}
              {cursorFrame && (
                <div
                  aria-hidden
                  data-agent-cursor
                  className={`${agentFx.agentCursor} ${cursorVanishing ? agentFx.agentCursorVanish : ''}`}
                  style={{
                    transform: `translate(${cursorFrame.x * scale}px, ${cursorFrame.y * scale}px)`,
                    opacity: 1,
                    zIndex: 950,
                    ['--cw' as string]: `${cursorFrame.w * scale}px`,
                    ['--ch' as string]: `${cursorFrame.h * scale}px`,
                  }}
                >
                  <div className={agentFx.agentCursorBox} />
                  <svg className={agentFx.agentCursorPointer} width="20" height="24" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 2.5 L5 21.5 L9.7 17 L12.6 23.4 L15.3 22.1 L12.5 15.9 L18.6 15.9 Z" fill="#FFFFFF" stroke="#1F2937" strokeWidth="1" strokeLinejoin="round" />
                  </svg>
                </div>
              )}

              {/* Live draft while dragging out an arrow/rect/ellipse. */}
              {draft &&
                (draft.tool === 'arrow' ? (
                  <SlideArrow key="draft" frame={draft.frame} color={DEFAULT_INK} strokeWidth={2} scale={scale} />
                ) : (
                  <div
                    key="draft"
                    className="pointer-events-none absolute z-20"
                    style={{
                      borderRadius: draft.tool === 'ellipse' ? '50%' : '2px',
                      left: normFrame(draft.frame).x * scale,
                      top: normFrame(draft.frame).y * scale,
                      width: normFrame(draft.frame).w * scale,
                      height: normFrame(draft.frame).h * scale,
                      border: `${Math.max(1.5, 2 * scale)}px solid ${DEFAULT_INK}`,
                    }}
                  />
                ))}
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default SlidesCanvas;
